"use client";

import { teamService } from "@/lib/services";
import { TeamMember } from "@/lib/types";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { Play, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

function TeamMemberSkeleton() {
	return (
		<div className="animate-pulse">
			<div className="flex flex-col items-center">
				<div className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 bg-gray-700 rounded-full mb-4 sm:mb-6" />
				<div className="h-6 sm:h-8 bg-gray-700 rounded w-36 sm:w-48 mb-2" />
				<div className="h-5 sm:h-6 bg-gray-700 rounded w-24 sm:w-32" />
			</div>
		</div>
	);
}

function EmptyState() {
	return (
		<div className="text-center py-8 sm:py-12 px-4">
			<div className="max-w-md mx-auto">
				<div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
						/>
					</svg>
				</div>
				<h3 className="text-lg sm:text-xl font-bold text-white mb-2">
					No Team Members Yet
				</h3>
				<p className="text-sm sm:text-base text-gray-400">
					Check back later to meet the RFFA team.
				</p>
			</div>
		</div>
	);
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="text-center py-8 sm:py-12 px-4">
			<div className="max-w-md mx-auto">
				<div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-8 h-8 sm:w-10 sm:h-10 text-red-500"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h3 className="text-lg sm:text-xl font-bold text-white mb-2">
					Oops! Something went wrong
				</h3>
				<p className="text-sm sm:text-base text-gray-400 mb-4">
					We couldn&apos;t load the team members.
				</p>
				<button
					onClick={onRetry}
					className="px-4 sm:px-6 py-2 bg-primary hover:bg-blue-700 text-white text-sm sm:text-base font-medium rounded-lg transition-colors"
				>
					Try Again
				</button>
			</div>
		</div>
	);
}

function TeamMemberModal({
	member,
	onClose,
}: {
	member: TeamMember;
	onClose: () => void;
}) {
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
			onClick={onClose}
		>
			<div
				className="bg-[#0000FF] rounded-xl sm:rounded-2xl max-w-5xl w-full h-[90vh] sm:h-[85vh] shadow-2xl animate-scale-in overflow-hidden border border-white/20"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					onClick={onClose}
					className="absolute top-3 right-3 sm:top-6 sm:right-6 w-8 h-8 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors z-10 shadow-lg"
					aria-label="Close modal"
				>
					<X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
				</button>

				<div className="grid grid-cols-1 md:grid-cols-[400px_1fr] h-full">
					<div className="bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center p-6 sm:p-8 md:p-12">
						<div className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80">
							<Image
								src={member.image_url || "/assets/team/placeholder.png"}
								alt={member.name}
								fill
								className="object-cover rounded-full shadow-2xl"
							/>
						</div>
					</div>

					<div className="overflow-y-auto p-6 sm:p-8 md:p-12">
						<div className="max-w-2xl">
							<div className="border-l-4 sm:border-l-8 border-white pl-4 sm:pl-6 mb-6 sm:mb-8">
								<h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3">
									{member.name}
								</h3>
								<p className="text-white font-bold text-sm sm:text-base">
									{member.title}
								</p>
							</div>

							<div className="space-y-4 sm:space-y-6">
								<div>
									<h4 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">
										About
									</h4>
									<p className="text-white leading-relaxed text-sm sm:text-base lg:text-lg">
										{member.description}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function VideoTeamSection() {
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [currentSlide, setCurrentSlide] = useState(0);
	const [showVideo, setShowVideo] = useState(false);
	const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

	const videoUrl =
		"https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

	const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
		{
			initial: 0,
			loop: true,
			slideChanged(slider) {
				setCurrentSlide(slider.track.details.rel);
			},
		},
		[
			(slider) => {
				let timeout: ReturnType<typeof setTimeout>;
				let mouseOver = false;

				function clearNextTimeout() {
					clearTimeout(timeout);
				}

				function nextTimeout() {
					clearTimeout(timeout);
					if (mouseOver) return;
					timeout = setTimeout(() => {
						slider.next();
					}, 3000);
				}

				slider.on("created", () => {
					slider.container.addEventListener("mouseover", () => {
						mouseOver = true;
						clearNextTimeout();
					});
					slider.container.addEventListener("mouseout", () => {
						mouseOver = false;
						nextTimeout();
					});
					nextTimeout();
				});

				slider.on("dragStarted", clearNextTimeout);
				slider.on("animationEnded", nextTimeout);
				slider.on("updated", nextTimeout);
			},
		]
	);

	const fetchTeamMembers = async () => {
		setLoading(true);
		setError(false);

		try {
			const response = await teamService.getAll();

			if (response.success && response.data) {
				setTeamMembers(response.data);
			} else {
				setError(true);
			}
		} catch (err) {
			console.error("Error fetching team members:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchTeamMembers();
	}, []);

	const show = false;

	return (
		<section className="py-12 sm:py-16 md:py-24 bg-[#0000FF]">
			<div className="container mx-auto px-4">
				{show && (
					<div className="mb-12 sm:mb-16">
						<div className="relative h-64 sm:h-80 md:h-96 lg:h-[400px] rounded-xl sm:rounded-2xl overflow-hidden">
							{!showVideo ? (
								<>
									<Image
										src="/assets/grass.png"
										alt="Football field"
										fill
										className="object-cover"
									/>

									<div className="absolute inset-0 bg-black/70" />

									<button
										onClick={() => setShowVideo(true)}
										className="absolute inset-0 flex items-center flex-col gap-3 sm:gap-4 justify-center group"
										aria-label="Play video"
									>
										<div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full border-2 sm:border-4 border-white flex items-center justify-center group-hover:scale-110 transition-transform bg-primary/20 backdrop-blur-sm">
											<Play className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white fill-white ml-1" />
										</div>

										<p className="text-white text-sm sm:text-base px-4 text-center">
											Watch the Future in Motion
										</p>
									</button>
								</>
							) : (
								<div className="relative w-full h-full">
									<video
										controls
										autoPlay
										className="w-full h-full object-cover"
										src={videoUrl}
									>
										Your browser does not support the video tag.
									</video>
									<button
										onClick={() => setShowVideo(false)}
										className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
										aria-label="Close video"
									>
										<X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
									</button>
								</div>
							)}
						</div>
					</div>
				)}

				<div>
					<h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-8 sm:mb-12 uppercase px-4">
						OUR TEAM
					</h2>

					<div className="max-w-5xl mx-auto">
						{loading && <TeamMemberSkeleton />}

						{error && !loading && <ErrorState onRetry={fetchTeamMembers} />}

						{!loading && !error && teamMembers.length === 0 && <EmptyState />}

						{!loading && !error && teamMembers.length > 0 && (
							<div>
								<div ref={sliderRef} className="keen-slider">
									{teamMembers.map((member, idx) => (
										<div
											key={idx}
											className="keen-slider__slide flex justify-center px-4"
										>
											<button
												onClick={() => setSelectedMember(member)}
												className="flex flex-col items-center group cursor-pointer"
											>
												<div className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 mb-4 sm:mb-6">
													<Image
														src={
															member.image_url || "/assets/team/placeholder.png"
														}
														alt={member.name}
														fill
														className="object-cover rounded-full transition-transform duration-300 group-hover:scale-105"
													/>
													<div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
														<span className="text-white text-sm sm:text-base font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-4 text-center">
															View Details
														</span>
													</div>
												</div>

												<h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-white/90 transition-colors text-center px-2">
													{member.name}
												</h3>
												<p className="text-white font-bold text-sm sm:text-base text-center px-2">
													{member.title}
												</p>
											</button>
										</div>
									))}
								</div>

								{teamMembers.length > 1 && (
									<div className="flex justify-center gap-2 sm:gap-3 mt-6 sm:mt-8">
										{teamMembers.map((_, idx) => (
											<button
												key={idx}
												onClick={() => {
													instanceRef.current?.moveToIdx(idx);
												}}
												className={`transition-all duration-300 rounded-full ${
													currentSlide === idx
														? "w-8 sm:w-12 h-2 sm:h-3 bg-white"
														: "w-2 sm:w-3 h-2 sm:h-3 bg-white/40 hover:bg-white/60"
												}`}
												aria-label={`Go to team member ${idx + 1}`}
											/>
										))}
									</div>
								)}

								<div className="text-center text-white/70 text-xs sm:text-sm mt-3 sm:mt-4">
									{currentSlide + 1} of {teamMembers.length}
								</div>
							</div>
						)}
					</div>
				</div>

				{selectedMember && (
					<TeamMemberModal
						member={selectedMember}
						onClose={() => setSelectedMember(null)}
					/>
				)}
			</div>
		</section>
	);
}
